/**
 * PO status roll-up (card i-salOBh3pft).
 *
 * When every line item on a purchase order shares ONE status and the PO is not
 * already in the matching PO status, the app offers (never forces) to move the
 * PO to it. This pure helper is the one rule every line-status entry point uses
 * (via PoStatusRollupProvider).
 *
 * Which lines count: EVERY line on the PO, none excluded — the same rule the
 * old Received-only prompt used (lineItems.every(...)), and the same set the
 * receive service's header roll-up and the auto_downgrade trigger count.
 * A Cancelled or Broken line therefore blocks the prompt unless every line is
 * Cancelled (→ Cancelled) or Broken (→ no prompt: no PO status).
 *
 * Line → PO mapping: the PO and line status sets differ, so match by CODE
 * (case-insensitive) first, then by NAME — the same name match the PO → lines
 * cascade dialog uses (F-3), which is what pairs line CREATED "Draft" with PO
 * DRAFT and line BACK_ORDERED "Backorder" with PO BACKORDER. A line status with
 * no PO counterpart (Broken, Pulled) returns null.
 */

export interface RollupLineStatus {
  code?: string | null;
  name?: string | null;
}

export interface RollupPoStatus {
  id: string;
  code?: string | null;
  name?: string | null;
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/**
 * Pass the PO's status id, each line's status AFTER the change, and the org's
 * PO statuses. Returns the PO status to offer, or null when there are no lines,
 * any line has no status, the lines differ, no PO status matches, or the PO is
 * already in it.
 */
export function getPoRollupStatus<S extends RollupPoStatus>(
  poStatusId: string | null | undefined,
  lines: ReadonlyArray<RollupLineStatus>,
  poStatuses: ReadonlyArray<S>,
): S | null {
  if (lines.length === 0) {return null;}
  const code = norm(lines[0].code);
  if (!code) {return null;}
  if (lines.some((l) => norm(l.code) !== code)) {return null;}
  const name = norm(lines[0].name);
  const target =
    poStatuses.find((s) => norm(s.code) === code) ??
    (name ? poStatuses.find((s) => norm(s.name) === name) : undefined);
  if (!target || target.id === poStatusId) {return null;}
  return target;
}
